import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useMessageTemplates, renderMessage, firstName } from "../lib/messageTemplates";
import {
  X,
  Printer,
  EnvelopeSimple,
  ChatText,
  Tag,
  PencilSimpleLine,
  CheckCircle,
  PaperPlaneTilt,
  CaretLeft,
} from "@phosphor-icons/react";

/**
 * Post-ticket quick actions. Opens automatically after a repair ticket is
 * created. Actions:
 *   - Print label (Dymo 1.125"×3.5")
 *   - Print ticket slip (80mm customer-pickup slip)
 *   - SMS ticket (intake confirmation + ticket number)
 *   - Email ticket (intake confirmation with slip PDF attached)
 *   - Edit ticket (navigate into the ticket detail page)
 *
 * If `tickets` is passed (multi-device batch intake), actions operate on
 * EVERY ticket in the batch (e.g. one label per ticket).
 */
export default function PostTicketActionsModal({
  open,
  onClose,
  ticket,
  tickets = null, // optional: full batch when multiple devices intaken
  customer,
  store,
}) {
  const nav = useNavigate();
  // Screen: "actions" | "sms" | "email"
  const [screen, setScreen] = useState("actions");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [smsMsg, setSmsMsg] = useState("");

  // Normalize inputs BEFORE any hook that reads them. Hooks must be called
  // in the same order every render, so nothing can live after an early
  // `return null`.
  const tplMap = useMessageTemplates();
  const batch = (tickets && tickets.length > 1) ? tickets : (ticket ? [ticket] : []);
  const isBatch = batch.length > 1;

  // Default SMS body — staff can edit in the sub-screen before sending.
  const defaultSms = (() => {
    if (!ticket) return "";
    const names = batch.map((t) => t.ticket_number).join(", ");
    if (isBatch) {
      return `${store?.name || "iRepair"}: your ${batch.length} devices have been booked in. Tickets ${names}. We'll be in touch with updates.`;
    }
    const device = [ticket.device_make, ticket.device_model].filter(Boolean).join(" ").trim();
    const saved = tplMap?.ticket_booked?.sms;
    if (saved) {
      return renderMessage(saved, {
        customer: firstName(ticket.customer_name), device: device || "device", ticket: ticket.ticket_number,
        estimate: Number(ticket.estimated_cost || 0).toFixed(2), store: store?.name || "", store_phone: store?.phone || "",
      });
    }
    return `${store?.name || "iRepair"}: thanks ${ticket.customer_name || ""}, your ${device || "device"} has been booked in as ${ticket.ticket_number}. We'll text you with updates.`;
  })();

  useEffect(() => {
    if (!open) return;
    setScreen("actions");
    setEmail(customer?.email || "");
    setPhone(customer?.phone || "");
    setSmsMsg(defaultSms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticket?.id, customer, tplMap]);

  if (!open || !ticket) return null;

  // Open each ticket's label PDF in a new window for immediate printing.
  const printAllLabels = async () => {
    try {
      for (const t of batch) {
        const resp = await api.get(`/repair-tickets/${t.id}/label.pdf`, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(resp.data);
        const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
        if (!w) {
          toast.error("Pop-up blocked — allow pop-ups to print labels.");
          return;
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't print label");
    }
  };

  const printAllSlips = async () => {
    try {
      for (const t of batch) {
        const resp = await api.get(`/repair-tickets/${t.id}/slip.pdf`, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(resp.data);
        const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
        if (!w) {
          toast.error("Pop-up blocked — allow pop-ups to print slips.");
          return;
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
      }
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't print slip");
    }
  };

  // Send one SMS per ticket (or just one when there's a single ticket).
  // We only send to the primary number on the primary ticket — batched
  // intake all belongs to the same customer.
  const sendSms = async () => {
    if (!phone) return toast.error("Enter a phone number");
    if (!smsMsg.trim()) return toast.error("Message cannot be empty");
    setSending(true);
    try {
      await api.post(`/repair-tickets/${ticket.id}/sms`, {
        to: phone,
        message: smsMsg,
        template_key: "intake_confirmation",
      });
      toast.success(`SMS sent to ${phone}`);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const sendEmail = async () => {
    if (!email) return toast.error("Enter an email address");
    setSending(true);
    try {
      await api.post(`/repair-tickets/${ticket.id}/email`, { to: email });
      toast.success(`Emailed ticket to ${email}`);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const editTicket = () => {
    onClose?.();
    nav(`/repairs/${ticket.id}`);
  };

  const device = [ticket.device_make, ticket.device_model].filter(Boolean).join(" ").trim();

  return (
    <div
      className="fixed inset-0 z-[10020] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="post-ticket-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-3xl overflow-hidden"
        style={{ boxShadow: "0 24px 64px rgba(30, 95, 235, 0.25)" }}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-5 border-b border-[#F3F4F6]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0">
                <CheckCircle size={22} weight="fill" className="text-[#1E5FEB]" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">
                  {isBatch ? `${batch.length} tickets created` : "Ticket created"}
                </div>
                <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
                  <span className="font-display text-[22px] font-semibold tracking-tight text-[#121216]" data-testid="post-ticket-number">
                    {isBatch
                      ? `${batch[0].ticket_number} +${batch.length - 1}`
                      : ticket.ticket_number}
                  </span>
                  {device && !isBatch && (
                    <span className="text-[12px] text-[#4B5563] font-medium">{device}</span>
                  )}
                </div>
                {customer?.name && (
                  <div className="text-[12px] text-[#4B5563] mt-0.5 truncate">
                    {customer.name}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="post-ticket-close"
              className="p-2 rounded-full hover:bg-[#F8FAFC] text-[#4B5563]"
              aria-label="Close"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* Body */}
        {screen === "actions" && (
          <div className="p-6 space-y-5">
            <div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">
                Print
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ActionTile
                  testid="post-ticket-print-label"
                  icon={<Tag size={20} weight="bold" />}
                  label="Print label"
                  sub={isBatch ? `${batch.length} Dymo` : "Dymo"}
                  onClick={printAllLabels}
                />
                <ActionTile
                  testid="post-ticket-print-slip"
                  icon={<Printer size={20} weight="bold" />}
                  label="Print slip"
                  sub={isBatch ? `${batch.length} slips · 80mm` : "80mm thermal"}
                  onClick={printAllSlips}
                />
              </div>
            </div>

            <div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">
                Notify customer
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ActionTile
                  testid="post-ticket-sms"
                  icon={<ChatText size={20} weight="bold" />}
                  label="SMS"
                  sub="Intake confirmed"
                  onClick={() => setScreen("sms")}
                  disabled={!customer?.phone && !ticket.customer_phone}
                  disabledHint="Add a phone to the customer record first."
                />
                <ActionTile
                  testid="post-ticket-email"
                  icon={<EnvelopeSimple size={20} weight="bold" />}
                  label="Email"
                  sub="Intake + PDF"
                  onClick={() => setScreen("email")}
                  disabled={!customer?.email}
                  disabledHint="Add an email to the customer record first."
                />
              </div>
            </div>

            <div>
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-3">
                Workshop
              </div>
              <button
                type="button"
                onClick={editTicket}
                data-testid="post-ticket-edit"
                className="w-full h-14 rounded-2xl border border-[#E5E7EB] hover:border-[#121216] hover:bg-[#F8FAFC] active:scale-[0.98] flex items-center gap-3 px-5 text-[#121216] transition-all"
              >
                <PencilSimpleLine size={18} weight="bold" />
                <div className="text-left">
                  <div className="text-[13px] font-semibold leading-tight">Edit ticket</div>
                  <div className="text-[11px] text-[#9CA3AF] leading-tight mt-0.5">
                    Add notes, update status, attach photos…
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={onClose}
              data-testid="post-ticket-done"
              className="w-full h-12 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[14px] font-semibold transition-colors active:scale-[0.98]"
            >
              Done
            </button>
          </div>
        )}

        {screen === "sms" && (
          <InputScreen
            title="Text the customer"
            hint={`We'll send a short SMS confirming ${ticket.ticket_number} has been booked in.`}
            back={() => setScreen("actions")}
            action={sendSms}
            actionLabel={sending ? "Sending…" : "Send SMS"}
            sending={sending}
          >
            <label className="block">
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2">Phone</div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+61412345678"
                data-testid="post-ticket-sms-phone"
                className="w-full h-12 px-4 rounded-xl border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#DBEAFE] text-[14px] font-mono bg-white transition-all"
              />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2">Message</div>
              <textarea
                value={smsMsg}
                onChange={(e) => setSmsMsg(e.target.value)}
                rows={4}
                data-testid="post-ticket-sms-message"
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#DBEAFE] text-[13px] leading-relaxed bg-white transition-all resize-none"
              />
              <div className="text-[11px] text-[#9CA3AF] mt-1 font-mono">{smsMsg.length} chars</div>
            </label>
          </InputScreen>
        )}

        {screen === "email" && (
          <InputScreen
            title="Email the customer"
            hint="Sends an intake-confirmation email with the ticket slip PDF attached."
            back={() => setScreen("actions")}
            action={sendEmail}
            actionLabel={sending ? "Sending…" : "Send email"}
            sending={sending}
          >
            <label className="block">
              <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium mb-2">Email</div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
                data-testid="post-ticket-email-input"
                className="w-full h-12 px-4 rounded-xl border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#DBEAFE] text-[14px] bg-white transition-all"
              />
            </label>
          </InputScreen>
        )}
      </div>
    </div>
  );
}

function ActionTile({ icon, label, sub, onClick, disabled, disabledHint, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledHint : undefined}
      data-testid={testid}
      className={`group relative flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border transition-all text-center ${
        disabled
          ? "border-[#F3F4F6] bg-[#F8FAFC] text-[#9CA3AF] cursor-not-allowed"
          : "border-[#E5E7EB] hover:border-[#121216] hover:bg-[#F8FAFC] text-[#121216] active:scale-[0.98]"
      }`}
    >
      <div className="shrink-0">{icon}</div>
      <div>
        <div className="text-[13px] font-semibold leading-tight">{label}</div>
        <div className="text-[11px] opacity-70 leading-tight mt-0.5">{sub}</div>
      </div>
    </button>
  );
}

function InputScreen({ title, hint, back, action, actionLabel, sending, children }) {
  return (
    <div className="p-6 space-y-4">
      <button
        onClick={back}
        data-testid="post-ticket-back"
        className="inline-flex items-center gap-1 text-[12px] text-[#4B5563] hover:text-[#121216]"
      >
        <CaretLeft size={12} weight="bold" /> Back
      </button>
      <div>
        <div className="font-display text-[22px] font-semibold tracking-tight text-[#121216]">
          {title}
        </div>
        {hint && <div className="text-[12px] text-[#4B5563] leading-relaxed mt-1.5">{hint}</div>}
      </div>
      {children}
      <button
        onClick={action}
        disabled={sending}
        data-testid="post-ticket-send"
        className="w-full h-12 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-[#9CA3AF] text-white text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors active:scale-[0.98]"
      >
        <PaperPlaneTilt size={14} weight="bold" />
        {actionLabel}
      </button>
    </div>
  );
}
